'use client';

import type { CollectionItem } from '@chakra-ui/react';
import {
  Select as ChakraSelect,
  Portal,
  Span,
  Stack,
  useListCollection,
  Text,
  HStack,
} from '@chakra-ui/react';
import * as React from 'react';
import { EmptyStateList, InfoTip, Alert, CloseButton, labelStyles } from '.';
import { isPrimitiveList } from '../utils';

interface SelectTriggerProps extends ChakraSelect.ControlProps {
  clearable?: boolean;
}

export const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  SelectTriggerProps
>(function SelectTrigger(props, ref) {
  const { children, clearable, colorPalette, ...rest } = props;
  return (
    <ChakraSelect.Control {...rest}>
      <ChakraSelect.Trigger pr="2rem" ref={ref} color="red">
        {children}
      </ChakraSelect.Trigger>
      <ChakraSelect.IndicatorGroup>
        {clearable && <SelectClearTrigger />}
        <ChakraSelect.Indicator
          fontSize="xs"
          color={colorPalette ? colorPalette + '.fg' : 'fg'}
        />
      </ChakraSelect.IndicatorGroup>
    </ChakraSelect.Control>
  );
});

const SelectClearTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraSelect.ClearTriggerProps
>(function SelectClearTrigger(props, ref) {
  return (
    <ChakraSelect.ClearTrigger asChild {...props} ref={ref}>
      <CloseButton
        size="xs"
        variant="plain"
        focusVisibleRing="inside"
        focusRingWidth="2px"
        pointerEvents="auto"
      />
    </ChakraSelect.ClearTrigger>
  );
});

interface SelectContentProps extends ChakraSelect.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
}

export const SelectContent = React.forwardRef<
  HTMLDivElement,
  SelectContentProps
>(function SelectContent(props, ref) {
  const { portalled = true, portalRef, ...rest } = props;
  return (
    <Portal disabled={!portalled} container={portalRef}>
      <ChakraSelect.Positioner>
        <ChakraSelect.Content {...rest} ref={ref} style={{ zIndex: 2000 }} />
      </ChakraSelect.Positioner>
    </Portal>
  );
});

export const SelectItem = React.forwardRef<
  HTMLDivElement,
  ChakraSelect.ItemProps
>(function SelectItem(props, ref) {
  const { item, children, ...rest } = props;
  return (
    <ChakraSelect.Item key={item.value} item={item} {...rest} ref={ref}>
      {children}
      <ChakraSelect.ItemIndicator />
    </ChakraSelect.Item>
  );
});

interface SelectValueTextProps extends Omit<
  ChakraSelect.ValueTextProps,
  'children'
> {
  children?(items: CollectionItem[]): React.ReactNode;
}

export const SelectValueText = React.forwardRef<
  HTMLSpanElement,
  SelectValueTextProps
>(function SelectValueText(props, ref) {
  const { children, placeholder, ...rest } = props;

  const placeholderValue = placeholder ? placeholder : 'Select an option';

  return (
    <ChakraSelect.ValueText
      {...labelStyles.placeholder({})}
      {...rest}
      ref={ref}
    >
      <ChakraSelect.Context>
        {(select) => {
          const items = select.selectedItems;
          if (items.length === 0) return placeholderValue;
          if (children) return children(items);
          if (items.length === 1)
            return select.collection.stringifyItem(items[0]);
          return `${items.length} selected`;
        }}
      </ChakraSelect.Context>
    </ChakraSelect.ValueText>
  );
});

export const SelectRoot = React.forwardRef<
  HTMLDivElement,
  ChakraSelect.RootProps
>(function SelectRoot(props, ref) {
  return (
    <ChakraSelect.Root
      {...props}
      ref={ref}
      positioning={{ sameWidth: true, ...props.positioning }}
    >
      {props.asChild ? (
        props.children
      ) : (
        <>
          <ChakraSelect.HiddenSelect />
          {props.children}
        </>
      )}
    </ChakraSelect.Root>
  );
}) as ChakraSelect.RootComponent;

interface SelectItemGroupProps extends ChakraSelect.ItemGroupProps {
  label: React.ReactNode;
  infoTip?: string;
}

export const SelectItemGroup = React.forwardRef<
  HTMLDivElement,
  SelectItemGroupProps
>(function SelectItemGroup(props, ref) {
  const { children, label, infoTip, ...rest } = props;
  return (
    <ChakraSelect.ItemGroup {...rest} ref={ref}>
      <ChakraSelect.ItemGroupLabel>{label}</ChakraSelect.ItemGroupLabel>
      {infoTip && (
        <Span pointerEvents="auto">
          <InfoTip>
            <Alert>{infoTip}</Alert>
          </InfoTip>
        </Span>
      )}
      {children}
    </ChakraSelect.ItemGroup>
  );
});

interface SelectItemWithKeyProps extends ChakraSelect.ItemProps {
  item: any;
  keyForDisplay: string;
  keyForValue: string;
  infoTip?: string;
}

export const SelectItemWithKey = React.forwardRef<
  HTMLDivElement,
  SelectItemWithKeyProps
>(function SelectItemWithKey(
  { item, keyForDisplay, keyForValue, ...rest },
  ref,
) {
  return (
    <Stack gap="0" key={`stack-${item[keyForValue]}`}>
      <ChakraSelect.Item
        key={item[keyForValue]}
        item={{ value: item[keyForValue] }}
        {...rest}
        ref={ref}
      >
        {item[keyForDisplay]}
        <ChakraSelect.ItemIndicator />
      </ChakraSelect.Item>
    </Stack>
  );
});

export interface SelectWithActionItemType {
  label: string; // The display label for the item
  infoTip?: string;
  value: any; // The value associated with the item
  description: string | null; // Optional description for the item
  item: any; // The original item object
}

export interface SelectWithActionProps {
  items: any[];
  onSelect: (value: any) => void; // {value: number|string|number[]|string[], items: object|object[]} | number|string§
  isLoading?: boolean;
  keyForDisplay?: string | null;
  keyForValue?: string | null;
  value?: string[] | number[] | string | number; // external state for value
  defaultValue?: string[] | number[] | string | number; // Only string[] is supported for now
  label?: string;
  getEntireItem?: boolean;
  placeholder?: string | null;
  createDescription?: null | ((item: any) => string);
  multiple?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  onBlur?: () => void;
  useSelectAllOption?: boolean;
  triggerResetToDefault?: number;
  colorPalette?: string;
  children?: React.ReactNode;
  [string: string]: any;
}

export const SelectWithAction_Inner = React.forwardRef<
  HTMLDivElement,
  SelectWithActionProps
>(function SelectWithAction(
  {
    items,
    onSelect,
    keyForDisplay = null,
    keyForValue = null,
    defaultValue = [],
    value,
    label = null,
    infoTip = null,
    placeholder = null,
    getEntireItem = false,
    createDescription = null,
    multiple = false,
    disabled = false,
    clearable = true,
    onBlur = () => null,
    useSelectAllOption = false,
    triggerResetToDefault = 0,
    isLoading = false,
    colorPalette,
    children,
    ...rest
  },
  ref,
) {
  const isPrimitive = isPrimitiveList(items);
  const triggerResetToDefaultRef = React.useRef(triggerResetToDefault);
  const triggerSelectAllRef = React.useRef(false);
  const hasNormalised = React.useRef(false);

  const calculatePlaceholderValue: (selectedItems: any[]) => string =
    React.useCallback(
      (selectedItems: any[]) => {
        if (!selectedItems || selectedItems.length === 0)
          return placeholder || 'Select an option';
        if (selectedItems.length === 1)
          return isPrimitive
            ? String(selectedItems[0])
            : String(selectedItems[0][keyForDisplay!]);
        return `${selectedItems.length} selected`;
      },
      [placeholder, isPrimitive, keyForDisplay],
    );

  const { collection, set, clear, reset } = useListCollection({
    initialItems: items,
    itemToString: (item: any) => (isPrimitive ? item : item[keyForDisplay!]),
    itemToValue: (item) =>
      isPrimitive ? String(item) : String(item[keyForValue!]),
  });

  const {
    normalisedDefaultValue,
    isNumber,
    initialisedPlaceholderValue,
    memoDefaultItems,
    isMissingDefaultValue,
  }: {
    normalisedDefaultValue: string[];
    isNumber: boolean;
    initialisedPlaceholderValue: string;
    memoDefaultItems: any[];
    isMissingDefaultValue: boolean;
  } = React.useMemo(() => {
    const normalisedDefaultValue = normaliseDefaultValue(defaultValue);

    if (!items || !Array.isArray(items) || items.length <= 0) {
      set([]);
      return {
        normalisedDefaultValue: [],
        isNumber: false,
        initialisedPlaceholderValue: '',
        memoDefaultItems: [],
        isMissingDefaultValue: false,
      };
    }

    const isNumber = items.some((item) =>
      isPrimitive
        ? typeof item === 'number'
        : typeof item[keyForValue!] === 'number',
    );
    const itemsByValue = new Map<string, any>();
    for (const item of items) {
      const keyValue = isPrimitive ? String(item) : String(item[keyForValue!]);
      itemsByValue.set(keyValue, item);
    }

    const filteredDefaults: string[] = normalisedDefaultValue.filter((val) => {
      const keyVal = isNumber ? String(Number(val)) : String(val);
      return itemsByValue.has(keyVal);
    });

    const defaultItems =
      !isPrimitive && filteredDefaults.length > 0 && items.length > 0
        ? filteredDefaults.map((val) => {
            const keyVal = isNumber ? String(Number(val)) : String(val);
            return itemsByValue.get(keyVal);
          })
        : filteredDefaults;

    set(Array.isArray(items) ? (items as any[]) : []);

    return {
      normalisedDefaultValue: filteredDefaults,
      isNumber,
      initialisedPlaceholderValue: calculatePlaceholderValue(defaultItems),
      initialisedInputValue:
        filteredDefaults.length === 1 && items.length > 0
          ? !isPrimitive
            ? defaultItems[0]?.[keyForDisplay!]
            : defaultItems[0]
          : undefined,
      memoDefaultItems: Array.isArray(defaultItems)
        ? (defaultItems as any[])
        : [],
      isMissingDefaultValue:
        normalisedDefaultValue.length > 0 &&
        normalisedDefaultValue.length !== filteredDefaults.length,
    };
  }, [items]);

  const [internalValue, setInternalValue] = React.useState<string[]>(
    normalisedDefaultValue,
  );
  const [placeholderValue, setPlaceholderValue] = React.useState<string>(
    initialisedPlaceholderValue,
  );

  const getItemsForValues = React.useCallback(
    (values: string[]) =>
      isPrimitive
        ? values
        : items.filter((item: any) =>
            values.includes(String(item[keyForValue!])),
          ),
    [isPrimitive, items, keyForValue],
  );

  // Update value when normalisedDefaultValue changes and items are loaded
  // Update the default value only for the first time when the component is mounted
  React.useEffect(() => {
    if (hasNormalised.current) return;
    if (normalisedDefaultValue.length > 0) {
      setInternalValue(normalisedDefaultValue);
      setPlaceholderValue(initialisedPlaceholderValue);
      hasNormalised.current = true;
    }
  }, [normalisedDefaultValue]);

  React.useEffect(() => {
    if (value === undefined || value === null) return;
    const nextValue = normaliseDefaultValue(value);
    setInternalValue(nextValue);
    setPlaceholderValue(calculatePlaceholderValue(getItemsForValues(nextValue)));
    hasNormalised.current = true;
  }, [value, calculatePlaceholderValue, getItemsForValues]);

  React.useEffect(() => {
    if (isMissingDefaultValue) {
      onSelect(
        getEntireItem
          ? multiple
            ? isNumber
              ? {
                  value: normalisedDefaultValue.map(Number),
                  items: memoDefaultItems,
                }
              : { value: normalisedDefaultValue, items: memoDefaultItems }
            : isNumber
              ? {
                  value: Number(normalisedDefaultValue[0]) ?? undefined,
                  items: memoDefaultItems[0] ?? undefined,
                }
              : {
                  value: normalisedDefaultValue[0] ?? undefined,
                  items: memoDefaultItems[0] ?? undefined,
                }
          : multiple
            ? isNumber
              ? normalisedDefaultValue.map(Number)
              : normalisedDefaultValue
            : isNumber
              ? (Number(normalisedDefaultValue[0]) ?? undefined)
              : (normalisedDefaultValue[0] ?? undefined),
      );
    }
  }, [isMissingDefaultValue]);

  const allItemsSelected =
    multiple &&
    useSelectAllOption &&
    internalValue.length > 0 &&
    items.length > 0 &&
    internalValue.length === items.length &&
    internalValue.every((value) =>
      items.some((item: any) =>
        isPrimitive
          ? String(item) === value
          : String(item[keyForValue!]) === value,
      ),
    )
      ? true
      : false;

  // const [isCleared, setIsCleared] = React.useState<boolean>(false);

  // const allItemValues = items.map((item: any) =>
  //   isPrimitive ? String(item) : String(item[keyForValue!]),
  // );
  // const isAllSelected =
  //   allItemValues.length > 0 &&
  //   allItemValues.every((val: string) =>
  //     (Array.isArray(value) ? value : [value as unknown as string])
  //       .map(String)
  //       .includes(val),
  //   );

  // Update collection when items change - DISABLED FOR DEBUGGING
  // const itemsRef = React.useRef(items);
  // React.useEffect(() => {
  //   if (JSON.stringify(items) !== JSON.stringify(itemsRef.current)) {
  //     itemsRef.current = items;
  //     set(items);
  //   }
  // }, [items]);

  // Update placeholder when items or other dependencies change
  // React.useEffect(() => {
  //   if (!isCleared) {
  //     setPlaceholderValue(
  //       calculatePlaceholderValue(
  //         !isPrimitive && normalisedDefaultValue.length > 0 && items.length > 0
  //           ? items.filter(
  //               (item: any) =>
  //                 String(item[keyForValue!]) === normalisedDefaultValue[0],
  //             )
  //           : normalisedDefaultValue,
  //       ),
  //     );
  //   }
  // }, [isCleared]);

  const handleOnSelect = (values: string[], items: any[]) => {
    setInternalValue(values);

    onSelect(
      getEntireItem
        ? multiple
          ? isNumber
            ? { value: values.map(Number), items: items }
            : { value: values, items: items }
          : isNumber
            ? {
                value: Number(values[0]) ?? undefined,
                items: items[0] ?? undefined,
              }
            : { value: values[0] ?? undefined, items: items[0] ?? undefined }
        : multiple
          ? isNumber
            ? values.map(Number)
            : values
          : isNumber
            ? (Number(values[0]) ?? undefined)
            : (values[0] ?? undefined),
    );
  };

  const handleSelectAll = () => {
    triggerSelectAllRef.current = true;

    if (!allItemsSelected) {
      // Select all
      setPlaceholderValue(`${items.length} selected`);
      handleOnSelect(
        items.map((item: any) =>
          isPrimitive ? String(item) : String(item[keyForValue!]),
        ),
        items,
      );
    } else {
      // Deselect all
      setPlaceholderValue(placeholder || `Select ${label ?? 'an option'}`);
      handleOnSelect([], []);
    }
  };

  const handleOnChange = (e: any) => {
    if (!triggerSelectAllRef.current) {
      setPlaceholderValue(calculatePlaceholderValue(e.items));
      handleOnSelect(e.value as string[], e.items);
    } else triggerSelectAllRef.current = false;
  };

  // const handleOnChange = (e: any) => {
  //   const nextValue: string[] = Array.isArray(e.value)
  //     ? e.value
  //     : e.value == null
  //       ? []
  //       : [e.value];
  //   setPlaceholderValue(calculatePlaceholderValue(e.items));
  //   setValue(nextValue);
  //   onSelect(
  //     getEntireItem
  //       ? multiple
  //         ? { value: nextValue, items: e.items }
  //         : { value: nextValue[0], items: e.items[0] }
  //       : multiple
  //         ? nextValue
  //         : nextValue[0],
  //   );
  // };

  const handleClear = () => {
    // setIsCleared(true);
    setPlaceholderValue(placeholder || `Select ${label ?? 'an option'}`);
    handleOnSelect([], []);
  };

  React.useEffect(() => {
    if (triggerResetToDefaultRef.current === triggerResetToDefault - 1) {
      setPlaceholderValue(initialisedPlaceholderValue);
      // setIsCleared(false);
      handleOnSelect(
        normalisedDefaultValue as string[],
        isPrimitive
          ? normalisedDefaultValue
          : items.filter((item: any) =>
              normalisedDefaultValue.includes(item[keyForValue!]),
            ),
      );
      triggerResetToDefaultRef.current = triggerResetToDefault;
    }
  }, [triggerResetToDefault]);

  return (
    <SelectRoot
      ref={ref}
      collection={collection}
      onValueChange={handleOnChange}
      multiple={multiple}
      disabled={disabled || collection.items.length === 0}
      value={internalValue}
      onInteractOutside={onBlur}
      justifyContent="center"
      defaultValue={normalisedDefaultValue}
      {...(label && { position: 'relative' })}
      {...rest}
      colorPalette={colorPalette}
    >
      <ChakraSelect.HiddenSelect />
      {label && (
        <ChakraSelect.Label
          {...(labelStyles.floating({
            isBorder: false,
            colorPalette,
          }) as any)}
        >
          {label}
          {infoTip && (
            <Span pointerEvents="auto">
              <InfoTip>
                <Alert>{infoTip}</Alert>
              </InfoTip>
            </Span>
          )}
        </ChakraSelect.Label>
      )}
      <ChakraSelect.Control
        colorPalette={colorPalette}
        // {...(labelStyles.border({ colorPalette }) as any)}
      >
        <ChakraSelect.Trigger gap={3}>
          <ChakraSelect.ValueText>
            <HStack gap={2}>
              {children && (
                <Text {...labelStyles({ colorPalette })}>{children}</Text>
              )}
              <Text {...labelStyles.placeholder({ colorPalette })}>
                {collection.items.length === 0
                  ? 'List is empty'
                  : placeholderValue}
              </Text>
            </HStack>
          </ChakraSelect.ValueText>

          <ChakraSelect.IndicatorGroup padding={0} gap={0}>
            {clearable && (
              <ChakraSelect.ClearTrigger
                onClick={handleClear}
                fontSize="2xs"
                color={colorPalette ? colorPalette + '.subtle' : 'fg.subtle'}
                asChild
              />
            )}
            <ChakraSelect.Indicator
              fontSize="2xs"
              color={colorPalette ? colorPalette + '.subtle' : 'fg.subtle'}
            />
          </ChakraSelect.IndicatorGroup>
        </ChakraSelect.Trigger>
      </ChakraSelect.Control>
      <SelectContent>
        {useSelectAllOption && multiple && (
          <SelectItem
            key={'__select_all__'}
            item={{
              value: '__select_all__',
              label: allItemsSelected ? 'Deselect All' : 'Select All',
            }}
            onClick={handleSelectAll}
          >
            <Stack width="100%" direction="row" align="center">
              <SelectItemText color="primary.fg">
                {allItemsSelected ? 'Deselect All' : 'Select All'}
              </SelectItemText>
              {allItemsSelected && (
                <Span color="red.500" fontSize="md" fontWeight="bold">
                  ×
                </Span>
              )}
            </Stack>
          </SelectItem>
        )}
        {collection.items.map((item) => (
          <SelectItem
            item={item}
            key={`${isPrimitive ? item : item[keyForValue!]}`}
          >
            <Stack gap="0">
              <SelectItemText>
                {String(isPrimitive ? item : item[keyForDisplay!])}
              </SelectItemText>
              {createDescription && (
                <Span color="fg.subtle" textStyle="caption.muted">
                  {createDescription(item)}
                </Span>
              )}
            </Stack>
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
});

export const SelectWithAction = React.forwardRef<
  HTMLDivElement,
  SelectWithActionProps
>(function SelectWithAction(props, ref) {
  return Array.isArray(props.items) && props.items.length > 0 ? (
    <SelectWithAction_Inner ref={ref} {...props} />
  ) : (
    <EmptyStateList isLoading={props.isLoading} title="No items to display" />
  );
});

export const SelectItemText = ChakraSelect.ItemText;

const normaliseDefaultValue = (
  defaultValue: string[] | number[] | string | number,
): string[] => {
  if (Array.isArray(defaultValue)) {
    if (defaultValue.length <= 0) return [];
    return defaultValue.map((value) => String(value));
  }
  if (!String(defaultValue)) return [];
  return [String(defaultValue)];
};
